import { ParsedUrlQuery } from 'querystring';

import {
  Dispatch,
  Fragment,
  MutableRefObject,
  SetStateAction,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';

import { useParams, useSearchParams } from 'next/navigation';

import { ListingFilterItemKey } from '@components/ui/filters/MultiSelectionFilter/types';
import { clearQueryParamsFilters } from '@components/views/hotel/utils';
import { FilterContextType, useFiltersContext } from '@contexts/Filters';
import clean from '@utils/cleanObject';
import { paramsToObject } from '@components/views/utils';
import useRouterNext from '@hooks/useRouterNext';

import { ListingFiltersWrapper } from './ListingFiltersStyle';

type FilterKeyValueModel = {
  key: string;
  value: string;
  selected: boolean;
  count: number;
};
export type EntityFilter = {
  type?: string;
  items: FilterKeyValueModel[];
  title?: string;
};

const getAvailableFilters = (entityFilters?: Array<EntityFilter>) => {
  return (
    entityFilters
      ?.map((ef) => ef?.type)
      .reduce((acc, type) => {
        acc[type as string] = type;
        return acc;
      }, {} as Record<string, string | undefined>) ?? {}
  );
};

const useLocalState = ({
  initialKeyword = '',
  initialMinPrice,
  initialMaxPrice,
}: {
  initialKeyword: string;
  initialMinPrice: number | undefined;
  initialMaxPrice: number | undefined;
}) => {
  // ================================================
  // Local State variables to keep track of local data
  // ================================================
  const [keywords, setKeywords] = useState<string>(initialKeyword);
  const [minAccPriceValue, setMinAccPriceValue] = useState<number | undefined>(
    initialMinPrice
  );
  const [maxAccPriceValue, setMaxAccPriceValue] = useState<number | undefined>(
    initialMaxPrice
  );
  const [onlyAvailableHotels, setOnlyAvailableHotels] =
    useState<FilterKeyValueModel>();
  const [segment, setSegment] = useState<FilterKeyValueModel[]>();
  const [freeCancellation, setFreeCancellation] =
    useState<FilterKeyValueModel>();
  const [themes, setThemes] = useState<FilterKeyValueModel[]>();
  const [departures, setDepartures] = useState<FilterKeyValueModel[]>();
  const [transportations, setTransportations] =
    useState<FilterKeyValueModel[]>();
  const [specialPeriods, setSpecialPeriods] = useState<FilterKeyValueModel[]>();
  const [visas, setVisas] = useState<FilterKeyValueModel[]>();

  const [categories, setCategories] = useState<FilterKeyValueModel[]>();

  const [regions, setRegions] = useState<FilterKeyValueModel[]>();
  const [features, setFeatures] = useState<FilterKeyValueModel[]>();
  const [popularities, setPopularities] = useState<FilterKeyValueModel[]>();
  const [dates, setDates] = useState<FilterKeyValueModel[]>();
  const [boardTypes, setBoardTypes] = useState<FilterKeyValueModel[]>();
  const [stars, setStars] = useState<FilterKeyValueModel[]>();
  const [accommodationTypes, setAccommodationTypes] =
    useState<FilterKeyValueModel[]>();
  const [filtersData, setFilterData] = useState<any>({});
  return {
    keywords,
    setKeywords,
    minAccPriceValue,
    setMinAccPriceValue,
    maxAccPriceValue,
    setMaxAccPriceValue,
    onlyAvailableHotels,
    setOnlyAvailableHotels,
    segment,
    setSegment,
    freeCancellation,
    setFreeCancellation,
    themes,
    setThemes,
    departures,
    setDepartures,
    transportations,
    setTransportations,
    specialPeriods,
    setSpecialPeriods,
    visas,
    setVisas,
    categories,
    setCategories,
    boardTypes,
    setBoardTypes,
    features,
    setFeatures,
    popularities,
    setPopularities,
    dates,
    setDates,
    regions,
    setRegions,
    stars,
    setStars,
    accommodationTypes,
    setAccommodationTypes,
    filtersData,
    setFilterData,
  };
};

type LocalState = ReturnType<typeof useLocalState>;

const useRouterPush = () => {
  const router = useRouterNext();
  const { push: nextPush } = router;
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const push: typeof nextPush = (args) => {
    return nextPush(args);
  };
  return { query, push };
};
const useUpdaters = ({
  setOnlyAvailableHotels,
  setFreeCancellation,
  setFilters,
  basePath,
  setIsRedirecting,
  setCheckedKeys,
}: Pick<LocalState, 'setOnlyAvailableHotels' | 'setFreeCancellation'> & {
  basePath?: string;
  setIsRedirecting?: (value: boolean) => void;
  setCheckedKeys?: Dispatch<SetStateAction<any[] | undefined>>;
} & Pick<FilterContextType, 'setFilters'>) => {
  const { push } = useRouterPush();
  const searchParams = useSearchParams();

  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const params = useParams();
  const pathname = basePath ?? (params?.slug as string);

  // ================================================
  // Handling onCheck for the region filter
  // ================================================
  const onRegionChecked = (checkedKeysValue: string): void => {
    const { ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      region: checkedKeysValue,
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };
  // ================================================
  // Handle onCHeck for the themes filter
  // ================================================
  const handleSelectedThemes = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      theme: itemKeys as string[],
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  // ================================================
  // Handle onCHeck for the departures filter
  // ================================================
  const handleSelectedDepartures = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      departure: itemKeys as string[],
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  // ================================================
  // Handle onCHeck for the transportations filter
  // ================================================
  const handleSelectedTransportations = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      transportation: itemKeys as string[],
    });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  // ================================================
  // Handle onCHeck for the specialPeriods filter
  // ================================================
  const handleSelectedSpecialPeriods = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      specialPeriod: itemKeys as string[],
    });
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  // ================================================
  // Handle onCHeck for the visas filter
  // ================================================
  const handleSelectedVisas = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      visa: itemKeys as string[],
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  // ================================================
  // Handle onCHeck for the categories filter
  // ================================================
  const handleSelectedCategories = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      category: itemKeys as string[],
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  const handleSelectedSegments = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      segment: itemKeys as string[],
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  const handleSelectedFeatures = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      feature: itemKeys as string[],
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };
  const handleSelectedDates = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;

    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      date: itemKeys as string[],
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  const handleSelectedPopularities = (itemKeys: ListingFilterItemKey[]) => {
    const { ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      popularity: itemKeys as string[],
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  const handleSelectedTripAdvisor = (point: string) => {
    const { ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      point: point,
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
    setFilters((prevFilter) => ({
      ...prevFilter,
      selectedTripAdvisorItem: point,
    }));
  };

  const handleSelectedAccTypes = (itemKeys: ListingFilterItemKey[]) => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, boardType, ...nextQuery } = query;
    setIsRedirecting?.(true);

    const updatedQuery: Record<string, unknown> = { ...nextQuery };

    if (itemKeys.length > 0 && itemKeys.some((key) => key !== '')) {
      updatedQuery.boardType = itemKeys;
    }

    push({
      pathname,
      query: updatedQuery,
    });
  };

  const handleSingleSelectionFilter = (e: boolean, name: string) => {
    let queryParams;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page, ...nextQuery } = query;
    setIsRedirecting?.(true);
    if (name === 'status') {
      queryParams = { ...nextQuery, status: e };
      setFilters((prevFilter) => ({
        ...prevFilter,
        onlyAvailableProperties: e,
      }));

      setOnlyAvailableHotels((prev) => ({
        ...(prev as FilterKeyValueModel),
        selected: e,
      }));
    } else if (name === 'refundable') {
      queryParams = { ...nextQuery, refundable: e };
      setFilters((prevFilter) => ({
        ...prevFilter,
        freeCancellation: e,
      }));
      setFreeCancellation((prev) => ({
        ...(prev as FilterKeyValueModel),
        selected: e,
      }));
    }

    push({
      pathname: `/${params?.slug}`,
      query: clean(queryParams),
    });
  };

  const handleKeywordsFilter = (keywords: string) => {
    const { ...nextQuery } = query;
    const cleanQuery = clean({ ...nextQuery, page: null, keywords });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  const handleClearKeywordsFilter = () => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { keywords, page, ...nextQuery } = query;
    setIsRedirecting?.(true);
    if (Object.keys(nextQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: clean({ ...nextQuery }),
      });
    }
  };

  const handlePriceFilter = (
    min: number | undefined,
    max: number | undefined
  ) => {
    const { ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      priceMin: min,
      priceMax: max,
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  const handleStarFilter = (star: number[]) => {
    const { ...nextQuery } = query;
    const cleanQuery = clean({
      ...nextQuery,
      page: null,
      star: star,
    });
    setIsRedirecting?.(true);
    if (Object.keys(cleanQuery).length === 0) {
      push({
        pathname: `/${params?.slug}`,
      });
    } else {
      push({
        pathname: `/${params?.slug}`,
        query: cleanQuery,
      });
    }
  };

  const handleClearAllFilters = () => {
    // Clear Context Filters
    setFilters((val) => ({
      ...val,
      selectedRegions: [],
      regions: [],
      freeCancellation: false,
      onlyAvailableProperties: false,
      hotelName: '',
      keywords: '',
      minAccommodationPrice: 0,
      maxAccommodationPrice: 0,
      selectedSegments: [],
      selectedThemeItems: [],
      selectedDepartureItems: [],
      selectedTransportationItems: [],
      selectedSpecialPeriodItems: [],
      selectedVisaItems: [],
      selectedCategoryItems: [],
      selectedFeaturesItems: [],
      selectedStars: [],
      selectedAccommodationTypes: [],
      selectedDateItems: [],
      selectedTripAdvisorItem: '',
    }));
    setCheckedKeys?.([]);
    // Clear Query Params from Filters
    const nextQuery = clearQueryParamsFilters(query);
    setIsRedirecting?.(true);
    push({
      pathname: `/${params?.slug}`,
      query: nextQuery,
    });
  };
  return {
    handlePriceFilter,
    handleKeywordsFilter,
    handleSingleSelectionFilter,
    handleSelectedAccTypes,
    handleSelectedFeatures,
    handleSelectedDates,
    handleSelectedThemes,
    handleSelectedDepartures,
    handleSelectedTransportations,
    handleSelectedSpecialPeriods,
    handleSelectedVisas,
    handleSelectedCategories,
    onRegionChecked,
    handleClearKeywordsFilter,
    handleClearAllFilters,
    handleSelectedPopularities,
    handleStarFilter,
    handleSelectedTripAdvisor,
    handleSelectedSegments,
  };
};

type Updaters = ReturnType<typeof useUpdaters>;

export type RenderParams = LocalState &
  Updaters &
  Pick<FilterContextType, 'filters'>;

export type FiltersBlockProps = {
  applicableFilters: EntityFilter[] | undefined;
  children: (config: RenderParams) => React.ReactElement;
  basePath?: string;
  noWrapper?: boolean;
  filterManagerRef?: MutableRefObject<RenderParams>;
  setIsRedirecting?: (value: boolean) => void;
  setCheckedKeys?: Dispatch<SetStateAction<unknown[] | undefined>>;
};

// ========================
// FilterBlock Starts here
// ========================
const FiltersBlock = ({
  applicableFilters,
  children,
  basePath,
  noWrapper,
  filterManagerRef,
  setIsRedirecting,
  setCheckedKeys,
}: FiltersBlockProps) => {
  const availableFilters = getAvailableFilters(applicableFilters);
  const searchParams = useSearchParams();
  const query: ParsedUrlQuery = useMemo(() => {
    return paramsToObject(searchParams?.entries());
  }, [searchParams]);
  const localState = useLocalState({
    initialKeyword: String(query.keywords ?? ''),
    initialMinPrice: parseFloat(query.priceMin as string),
    initialMaxPrice: parseFloat(query.priceMax as string),
  });
  const {
    setKeywords,
    setOnlyAvailableHotels,
    setSegment,
    setFreeCancellation,
    setThemes,
    setDepartures,
    setTransportations,
    setSpecialPeriods,
    setVisas,
    setCategories,
    setBoardTypes,
    setStars,
    setFilterData,
    setAccommodationTypes,
    setFeatures,
    setPopularities,
    setRegions,
    setMaxAccPriceValue,
    setMinAccPriceValue,
  } = localState;
  // ================================================
  // Using React Hooks (useRouter , useContext)
  // ================================================
  const { filters, setFilters } = useFiltersContext();
  const updaters = useUpdaters({
    setFilters,
    setFreeCancellation,
    setOnlyAvailableHotels,
    basePath,
    setIsRedirecting,
    setCheckedKeys,
  });
  const renderParams = {
    ...localState,
    ...updaters,
    filters,
  };
  useImperativeHandle(filterManagerRef, () => renderParams);
  // ================================================
  // using the useEffect to fill the component on component load
  // ================================================

  const key = JSON.stringify(applicableFilters);
  useEffect(() => {
    if (!applicableFilters) return;
    const hasStatus = !!availableFilters['status'];
    const hasSegment = !!availableFilters['segment'];
    const hasTheme = !!availableFilters['theme'];
    const hasDeparture = !!availableFilters['departure'];
    const hasTransportation = !!availableFilters['transportation'];
    const hasSpecialPeriod = !!availableFilters['specialPeriod'];
    const hasVisa = !!availableFilters['visa'];
    const hasCategory = !!availableFilters['category'];
    const refundable = !!availableFilters['refundable'];
    const hasBoardType = !!availableFilters['boardType'];
    const hasRegion = !!availableFilters['region'];
    const hasFeature = !!availableFilters['feature'];
    const hasPopularity = !!availableFilters['popularity'];
    const hasStars = !!availableFilters['star'];
    const hasFilterData = !!availableFilters['date'];
    const hasTripAdvisor = !!availableFilters['point'];

    setFeatures(
      hasFeature
        ? applicableFilters
            ?.filter((item) => item?.type === 'feature')
            .reduce<FilterKeyValueModel[]>(
              (acc, item) => acc.concat(item.items),
              []
            )
        : []
    );

    setPopularities(
      hasPopularity
        ? applicableFilters?.filter((item) => item?.type === 'popularity')[0]
            ?.items
        : []
    );

    setRegions(
      hasRegion
        ? applicableFilters?.filter((item) => item?.type === 'region')[0]?.items
        : []
    );

    setOnlyAvailableHotels(
      hasStatus
        ? applicableFilters.filter((item) => item?.type === 'status')[0]
            ?.items[0]
        : ({} as FilterKeyValueModel)
    );

    setFreeCancellation(
      refundable
        ? applicableFilters.filter((item) => item?.type === 'refundable')[0]
            ?.items[0]
        : ({} as FilterKeyValueModel)
    );

    setThemes(
      hasTheme
        ? applicableFilters.filter((item) => item?.type === 'theme')[0]?.items
        : []
    );

    setDepartures(
      hasDeparture
        ? applicableFilters.filter((item) => item?.type === 'departure')[0]
            ?.items
        : []
    );

    setTransportations(
      hasTransportation
        ? applicableFilters.filter((item) => item?.type === 'transportation')[0]
            ?.items
        : []
    );

    setSpecialPeriods(
      hasSpecialPeriod
        ? applicableFilters.filter((item) => item?.type === 'specialPeriod')[0]
            ?.items
        : []
    );

    setVisas(
      hasVisa
        ? applicableFilters.filter((item) => item?.type === 'visa')[0]?.items
        : []
    );

    setCategories(
      hasCategory
        ? applicableFilters.filter((item) => item?.type === 'category')[0]?.items
        : []
    );

    setBoardTypes(
      hasBoardType
        ? applicableFilters.filter((item) => item?.type === 'boardType')[0]
            ?.items
        : []
    );
    setSegment(
      hasSegment
        ? applicableFilters.filter((item) => item?.type === 'segment')[0]?.items
        : []
    );
    setStars(
      hasStars
        ? applicableFilters.filter((item) => item?.type === 'star')[0]?.items
        : []
    );
    setFilterData(
      hasFilterData
        ? applicableFilters.filter((item) => item?.type === 'date')[0]?.items
        : []
    );

    setAccommodationTypes(
      hasTripAdvisor
        ? applicableFilters.filter((item) => item?.type === 'point')[0]?.items
        : []
    );
    const selectedRegion =
      applicableFilters
        ?.filter((item) => item?.type === 'region')[0]
        ?.items?.filter((item) => item.selected === true) || [];
    const selectedRegionsKey = selectedRegion.map((item) => item.key);
    if (query.keywords) {
      setKeywords(String(query.keywords));
    } else {
      setKeywords('');
    }

    if (query.priceMin) {
      setMinAccPriceValue(Number(query.priceMin as string));
    } else {
      setMinAccPriceValue(undefined);
    }

    if (query.priceMax) {
      setMaxAccPriceValue(Number(query.priceMax as string));
    } else {
      setMaxAccPriceValue(undefined);
    }

    setFilters((prevFilters) => ({
      ...prevFilters,
      freeCancellation: refundable
        ? applicableFilters.filter((item) => item.type === 'refundable')[0]
            .items[0].selected
        : undefined,
      onlyAvailableProperties: applicableFilters.filter(
        (item) => item?.type === 'status'
      )[0]?.items[0]?.selected,
      selectedRegions: hasRegion ? selectedRegionsKey : [],
      regions: applicableFilters?.filter((item) => item?.type === 'region')[0]
        ?.items,
      segment: hasSegment
        ? applicableFilters?.filter((item) => item?.type === 'segment')[0]?.items
        : [],
      selectedSegments: applicableFilters
        ?.filter((item) => item?.type === 'segment')[0]
        ?.items?.filter((item) => item.selected === true),
      selectedThemeItems: applicableFilters
        ?.filter((item) => item?.type === 'theme')[0]
        ?.items?.filter((item) => item.selected === true),
      selectedDepartureItems: applicableFilters
        ?.filter((item) => item?.type === 'departure')[0]
        ?.items?.filter((item) => item.selected === true),
      selectedTransportationItems: applicableFilters
        ?.filter((item) => item?.type === 'transportation')[0]
        ?.items?.filter((item) => item.selected === true),
      selectedSpecialPeriodItems: applicableFilters
        ?.filter((item) => item?.type === 'specialPeriod')[0]
        ?.items?.filter((item) => item.selected === true),
      selectedVisaItems: applicableFilters
        ?.filter((item) => item?.type === 'visa')[0]
        ?.items?.filter((item) => item.selected === true),
      selectedCategoryItems: applicableFilters
        ?.filter((item) => item?.type === 'category')[0]
        ?.items?.filter((item) => item.selected === true),
      selectedAccommodationTypes: hasBoardType
        ? applicableFilters
            ?.filter((item) => item?.type === 'boardType')[0]
            ?.items?.filter((item) => item.selected === true)
        : undefined,
      selectedFeaturesItems: applicableFilters
        ?.filter((item) => item?.type === 'feature')
        .flatMap((item) =>
          item?.items.filter((subItem) => subItem.selected === true)
        ),
      selectedDateItems: applicableFilters
        ?.filter((item) => item?.type === 'date')[0]
        ?.items.filter((item) => item.selected),
      selectedPopularitiesItems: applicableFilters
        ?.filter((item) => item?.type === 'popularity')[0]
        ?.items.filter((item) => item.selected === true),
      selectedStarsItem: applicableFilters
        ?.filter((item) => item?.type === 'star')[0]
        ?.items.filter((item) => item.selected === true),
      maxAccommodationPrice: Number(query.priceMax as string) || undefined,
      minAccommodationPrice: Number(query.priceMin as string) || undefined,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, key]);

  const Wrapper = noWrapper ? Fragment : ListingFiltersWrapper;
  return <Wrapper>{children(renderParams)}</Wrapper>;
};

export default FiltersBlock;
